import { Project } from '@/types/cmsTypes';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/cms/ui/status-badge';
import { PILL_BASE, TAG_PILL } from '@/components/cms/ui/pill-styles';
import { formatDistanceToNow } from 'date-fns';
import { formatDuration } from '@/modules/storyData/utils';
import { ClockIcon, MapPinLineIcon, TrashIcon } from '@phosphor-icons/react';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { getSquiggleCropStyle } from '@/utils/squiggleCrop';
import { SquiggleCorner } from '@/components/ui/squiggle-corner';
import { useState, useRef } from 'react';
import { IconButton } from '@/components/ui/icon-button';
import { CardTitle } from '@/components/ui/text';
import { shownUrl } from '@/utils/localCopies';

interface ProjectCardProps {
  project: Project;
  onView: (project: Project) => void;
  onDelete?: (project: Project) => void;
}

export function ProjectCard({ project, onView, onDelete }: ProjectCardProps) {
  const [imageFailed, setImageFailed] = useState(false);
  const hasCoverImage = !!project.heroImage && project.heroImage !== projectPlaceholder && !imageFailed;
  const isPlaceholderImage = !hasCoverImage;
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    if (!onDelete) return;
    hoverTimeoutRef.current = setTimeout(() => {
      setShowDeleteButton(true);
    }, 1000);
  };

  const handleMouseLeave = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setShowDeleteButton(false);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(project);
    }
  };
  
  return (
    <Card elevation="high"
      padding="none"
      interactive
      className="group flex flex-col overflow-hidden"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onView(project)}>
      <div className="relative mb-2 sm:mb-4">
        <div className="relative h-40 w-full overflow-hidden">
          {hasCoverImage && (
            <img
              src={shownUrl(project.heroImage)}
              alt={project.title}
              loading="lazy"
              decoding="async"
              onError={() => setImageFailed(true)}
              className="h-full w-full object-cover brightness-lureo-photo-brightness transition-[scale,filter] duration-lureo-duration-slow ease-lureo-easing-standard group-hover:scale-lureo-photo-hover-scale group-hover:brightness-lureo-photo-hover-brightness"
            />
          )}
          {hasCoverImage ? (
            <SquiggleCorner id={project.id} shape="wide" />
          ) : (
            <div
              className="absolute inset-0 pointer-events-none"
              style={getSquiggleCropStyle(project.id, { scaleRange: [3, 4], size: 'quarter' })}
            />
          )}
        </div>
        {/* Show appropriate overlay based on image type */}
        {isPlaceholderImage ? (
          <div className="absolute inset-0 opacity-lureo-photo-shade bg-linear-to-t from-lureo-highlight/60 via-lureo-highlight/10 to-transparent" />
        ) : (
          <>
            <div className="absolute inset-0 bg-lureo-photo-veil" />
            <div className="absolute inset-0 opacity-lureo-photo-shade bg-linear-to-t from-lureo-photo-shade-tint/60 via-lureo-photo-shade-tint/10 to-transparent" />
          </>
        )}
        <div className="absolute right-2 top-2">
          <StatusBadge status={project.status} />
        </div>
        {/* Delete Button - Top Left */}
        {onDelete && (
          <IconButton
            label="Delete project"
            tone="danger-outline"
            onClick={handleDeleteClick}
            className={`absolute left-2 top-2 z-10 transition-opacity duration-lureo-duration-moderate focus-visible:pointer-events-auto focus-visible:opacity-100 ${
              showDeleteButton ? 'opacity-100' : 'opacity-0 pointer-events-none'
            }`}
          >
            <TrashIcon className="h-4 w-4" />
          </IconButton>
        )}
        <div className="absolute bottom-4 left-4 right-4">
          <CardTitle as="h3" tone={isPlaceholderImage ? 'accent' : 'emphasis'} className="mb-1 line-clamp-1 text-lureo-20">
            {project.title}
          </CardTitle>
          <div className={`flex items-center gap-3 text-lureo-14 md:gap-4 ${isPlaceholderImage ? 'text-lureo-emphasis-strong' : 'text-lureo-on-emphasis/80'} leading-5`}>
            <span className="flex items-center">
              <ClockIcon className="mr-1 h-4 w-4" />
              {formatDuration(project.duration)}
            </span>
            <span className="flex items-center">
              <MapPinLineIcon className="mr-1 h-4 w-4" />
              {project.storiesCount} {project.storiesCount === 1 ? 'story' : 'stories'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-1 flex-col px-5 pb-5 pt-1 sm:px-7 sm:pb-7">
        <div className="grow">
          <p className="mb-2 line-clamp-2 text-lureo-14 font-light text-lureo-ink leading-5">{project.description}</p>

          <div className="mb-2 flex flex-wrap gap-1">
            {project.tags.slice(0, 3).map((tag) => (
              <span key={tag} className={`${PILL_BASE} ${TAG_PILL}`}>
                {tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className={`${PILL_BASE} ${TAG_PILL}`}>
                +{project.tags.length - 3} more
              </span>
            )}
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-3">
          <div className="text-lureo-14 text-lureo-ink leading-5">
            Updated{' '}
            {(() => {
              const distance = formatDistanceToNow(project.updatedAt, {
                addSuffix: true,
                includeSeconds: true
              }).replace('about ', '');
              if (distance.includes('less than') && distance.includes('second')) {
                return 'just now';
              }
              return distance;
            })()}
          </div>
        </div>
      </div>
    </Card>
  );
}
